import { useEffect, useMemo, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, EmptyState } from '../../../components/ui'
import { resetSelection } from '../../../features/booking/selectionSlice'
import { useAppDispatch } from '../../../store/hooks'
import { useTenant } from '../../../tenant/useTenant'
import { useToast } from '../../../hooks/useToast'
import { googleCalendarUrl, icsDataUrl, type CalendarEvent } from '../../../utils/calendarLinks'
import { formatLongDate, formatSlotTime, slotDate } from '../../../utils/datetime'
import { formatDuration, formatPrice } from '../../../utils/format'
import { directionsUrl } from '../../../features/catalog/directions'
import { useSession } from '../../auth/useSession'
import { clearPersistedSelection } from '../useSelectionPersistence'
import { readRememberedBooking } from '../lastBooking'
import type { BookingSummary } from '../../../types/storefront'

/** G8 — the confirmation. The cart is emptied here, so the back button lands on
 *  an empty wizard that redirects to step 1 instead of resubmitting. */
export function DoneStep() {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const toast = useToast()
  const location = useLocation()

  const { isAuthenticated } = useSession()

  const fromState = (location.state as { booking?: BookingSummary } | null)?.booking ?? null
  // A refresh throws the router state away. `?ref=` says which booking this screen
  // is for, so the copy in sessionStorage can only stand in for the right one.
  const ref = new URLSearchParams(location.search).get('ref')
  const booking = useMemo(() => {
    if (fromState) return fromState
    const remembered = readRememberedBooking(slug)
    if (!remembered) return null
    return !ref || remembered.code === ref ? remembered : null
  }, [fromState, ref, slug])

  // The summary carries only the branch id and name; the address and coordinates
  // live in the tenant bootstrap.
  const branch = useMemo(
    () => bootstrap.branches.find((b) => b.id === booking?.branch.id) ?? null,
    [bootstrap.branches, booking],
  )

  useEffect(() => {
    dispatch(resetSelection())
    clearPersistedSelection(slug)
  }, [dispatch, slug])

  // OC-299 (Minh check 19/10): the calendar buttons are back.
  const event: CalendarEvent | null = useMemo(() => {
    if (!booking) return null
    const services = booking.services.map((s) => s.name).join(', ')
    return {
      title: `${bootstrap.vendor.name} — ${services}`,
      description: `Booking reference: ${booking.code}\n${services}`,
      location: booking.branch.name,
      start: booking.starts_at,
      end: booking.ends_at,
      uid: `booking-${booking.id}@${slug}`,
    }
  }, [booking, bootstrap.vendor.name, slug])

  if (!booking) {
    return (
      <div className="container-page py-section-gap-md">
        <EmptyState
          icon="event_busy"
          title="We couldn't find that booking"
          body="This booking session has ended. If you just booked, we'll still be in touch to confirm."
          action={
            <Button to={`/${slug}`} variant="outlined">
              Back to home
            </Button>
          }
        />
      </div>
    )
  }

  const duration = booking.services.reduce((sum, s) => sum + s.duration, 0)

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(booking.code)
      toast.success('Booking reference copied.')
    } catch {
      // Clipboard blocked (insecure context, or a browser that asks first): the
      // code is on screen in large type, so there is nothing to recover from.
      toast.show('Copy the reference from the screen — your browser blocked the clipboard.')
    }
  }

  const share = async () => {
    const text = `Appointment at ${bootstrap.vendor.name} — reference ${booking.code}, ${formatLongDate(
      slotDate(booking.starts_at),
    )} at ${formatSlotTime(booking.starts_at)}.`
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My appointment', text, url: window.location.href })
        return
      }
      await navigator.clipboard.writeText(`${text} ${window.location.href}`)
      toast.success('Booking details copied.')
    } catch {
      // A cancelled share dialog is not an error worth shouting about; only a
      // genuinely unavailable clipboard reaches here with nothing done.
      toast.show("Couldn't share. You can screenshot your booking reference instead.")
    }
  }

  return (
    <div className="container-page flex justify-center py-12 md:py-section-gap-md">
      <main className="flex w-full max-w-[600px] flex-col gap-12">
        {/* OC-299 (Minh check 19/10): no step rail or "Step 6 of 6" above the title. */}
        <header className="flex flex-col items-center gap-6 text-center">
          <div className="flex flex-col gap-2">
            <h1 className="font-display text-headline-lg-mobile text-primary md:text-headline-lg">
              Booking confirmed
            </h1>
            <p className="mx-auto mt-2 max-w-md font-body-lg text-body-lg text-on-surface-variant">
              Your appointment has been successfully scheduled. We look forward to seeing you at{' '}
              {booking.branch.name}.
            </p>
          </div>

          {/* The reference is what the customer reads out at the desk, so it is
              the one thing on this screen sized to be read from a phone at arm's
              length — and copyable, because most people will paste it somewhere. */}
          <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-6 py-5">
            <p className="font-label-caps text-label-caps uppercase tracking-widest text-outline">
              Booking reference
            </p>
            <p className="font-mono text-[28px] font-medium leading-none tracking-[0.12em] text-primary md:text-[32px]">
              {booking.code}
            </p>
            <button
              type="button"
              onClick={() => void copyCode()}
              className="flex items-center gap-1.5 font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
                content_copy
              </span>
              Copy code
            </button>
          </div>
        </header>

        <section className="relative overflow-hidden rounded-xl border border-primary/10 bg-surface-container-low p-8">
          <div
            aria-hidden="true"
            className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-primary to-primary-fixed-dim"
          />
          <div className="relative z-10 flex flex-col gap-8">
            <Row label="Services">
              <ul className="flex flex-col gap-3">
                {booking.services.map((service) => (
                  <li key={service.id} className="flex justify-between gap-4">
                    <span className="font-display text-headline-sm text-on-surface">
                      {service.name}
                      <span className="mt-1 block font-body-md text-body-md text-on-surface-variant">
                        {formatDuration(service.duration)} · {service.staff_name ?? 'Any specialist'}
                      </span>
                    </span>
                    <span className="shrink-0 font-body-md text-body-md text-on-surface-variant">
                      {formatPrice(service.price, bootstrap.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </Row>

            <Divider />

            <Row label="Date & time">
              <p className="font-display text-headline-sm text-on-surface">
                {formatLongDate(slotDate(booking.starts_at))}
              </p>
              <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
                {formatSlotTime(booking.starts_at)} – {formatSlotTime(booking.ends_at)} ·{' '}
                {formatDuration(duration)}
              </p>
            </Row>

            <Divider />

            <Row label="Location">
              <p className="font-display text-headline-sm text-on-surface">{booking.branch.name}</p>
              {branch?.address && (
                <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
                  {[branch.address, branch.city].filter(Boolean).join(', ')}
                </p>
              )}
              {branch && directionsUrl(branch) && (
                <a
                  href={directionsUrl(branch) ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
                >
                  <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
                    directions
                  </span>
                  Get directions
                </a>
              )}
            </Row>

            <Divider />

            <Row label="Total">
              <p className="font-display text-headline-sm text-on-surface">
                {formatPrice(booking.total, bootstrap.currency)}
              </p>
            </Row>
          </div>
        </section>

        {/* Three nowrap buttons do not fit the 600 px column side by side: the two
            calendar ones share a row, Share keeps the full width under them. */}
        <div className="flex flex-col gap-4">
          {event && (
            <div className="flex flex-col gap-4 sm:flex-row">
              <Button
                size="lg"
                variant="filled"
                icon="calendar_add_on"
                className="sm:flex-1"
                onClick={() => {
                  const link = document.createElement('a')
                  link.href = icsDataUrl(event)
                  link.download = `booking-${booking.code}.ics`
                  link.click()
                }}
              >
                Add to calendar
              </Button>
              <Button
                size="lg"
                variant="outlined"
                icon="event"
                className="sm:flex-1"
                onClick={() => window.open(googleCalendarUrl(event), '_blank', 'noopener')}
              >
                Google Calendar
              </Button>
            </div>
          )}
          <Button size="lg" variant="outlined" icon="share" onClick={() => void share()}>
            Share
          </Button>
        </div>

        <div className="flex flex-col items-center gap-4 text-center">
          {/* A signed-in customer has somewhere to go back to: the appointment
              itself, where it can be checked or cancelled later. A guest has no
              account to land in, so home is the only honest destination. */}
          {isAuthenticated && (
            <Button
              to={`/${slug}/account/bookings/${booking.id}`}
              size="lg"
              variant="tonal"
              icon="event_available"
              fullWidth
            >
              View my appointment
            </Button>
          )}
          <button
            type="button"
            onClick={() => navigate(`/${slug}`)}
            className="font-body-md text-body-md text-primary underline transition-colors hover:text-primary-container"
          >
            Return to home
          </button>
          {bootstrap.vendor.cancellation_policy && (
            <p className="font-body-md text-sm text-on-surface-variant">
              {bootstrap.vendor.cancellation_policy}
            </p>
          )}
        </div>
      </main>
    </div>
  )
}

function Divider() {
  return <div aria-hidden="true" className="h-px w-full bg-primary/10" />
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 font-label-caps text-label-caps uppercase text-outline">{label}</p>
      {children}
    </div>
  )
}
